import { z } from 'zod';
import { UuidV7Schema } from './common.js';
import { QualificationCategorySchema } from '../enums/qualification-category.js';

/**
 * Schema du payload Pub/Sub `doorly-qualification-sync`. Émis par
 * `hipto-crm-module` à chaque upsert de qualification callcenter pour un
 * lead d'origine Doorly. Le CRM résout côté lui :
 *
 *   - Le pivot Doorly : `qualification.deliveryId →
 *     hpg_outgoing_datas → raw_hpg_datas/hpg_datas.collectedDatas.doorly_lead_id`
 *   - La catégorie canonique : `qualification.qualificationMapping →
 *     hipto_sub_metric → mapping vers QualificationCategory`
 *
 * Doorly ne refait PAS le mapping de catégorie (consommation seule). Le
 * matching de lead se fait sur l'unique pivot `doorly_lead_id` : toutes les
 * livraisons passent par Hopti (qui préserve `collectedDatas`), donc le CRM
 * le résout systématiquement.
 *
 * Historique : le CRM émet aussi `hipto_uid`, `match_phone` et
 * `match_submission_date`, pivots de secours du pipeline Manager (qui
 * strippait `doorly_lead_id`). Manager est retiré (2026-08) et ces champs
 * ne sont plus consommés — ils traversent le `.passthrough()` sans effet.
 * En prod, 100 % des qualifs depuis le 01/08/2026 se résolvaient déjà par
 * `doorly_lead_id` seul.
 */
export const LeadQualificationSyncSchema = z
  .object({
    event_type: z.enum(['created', 'updated', 'deleted']),
    /** ID stable côté CRM (BigInt sérialisé en string) — sert à
     * l'idempotency côté Doorly. */
    qualification_id: z.string(),
    /** Timestamp d'émission par le CRM, ISO 8601. */
    timestamp: z.string(),
    /** Pivot unique : l'UUID v7 du lead Doorly. Résolu côté CRM depuis
     * `collectedDatas.doorly_lead_id` (préservé par le pipeline Hopti).
     * `null` = message non routable côté Doorly → log + ack silencieux
     * (les anciens pivots de secours Manager ne sont plus consommés). */
    doorly_lead_id: UuidV7Schema.nullable(),
    /** Discriminant d'origine posé par le CRM (seule source de vérité de
     *  l'origine : `doorly_lead_id != null || utm_source == 'doorly'`).
     *
     *  Le topic `doorly-qualification-sync` est MUTUALISÉ : le CRM y publie
     *  désormais TOUTES les qualifications callcenter (pas seulement les leads
     *  d'origine Doorly) pour alimenter Hopti (retour 2E2) et le pricing des
     *  leads à la perf. Doorly ne doit ingérer QUE le Doorly : le listener
     *  droppe tout message `false` AVANT toute résolution de lead
     *  (cf. `QualificationsSyncService.handleQualification`) — sinon le
     *  fallback `match_phone` (match par téléphone seul sur ±30j) rattacherait
     *  par erreur une qualif non-Doorly à un lead Doorly partageant le numéro.
     *
     *  `undefined`/absent = ancien CRM qui n'émettait QUE du Doorly → traité
     *  comme `true` (rétrocompat : aucun changement tant que le CRM
     *  n'estampille pas explicitement le champ). */
    is_doorly_origin: z.boolean().optional(),
    data: z
      .object({
        /** Libellé brut tel que reçu du callcenter (free text). */
        raw_label: z.string(),
        /** Catégorie normalisée Doorly. `null` si le CRM n'a pas su
         * mapper depuis sa table `qualification_mapping`. */
        category: QualificationCategorySchema.nullable(),
        /** Famille métrique côté CRM (`hipto_metric.value`, ex. "Refus
         * Argumenté", "Codification temporaire"). Affiché en hint admin
         * pour comprendre la classification source. */
        metric: z.string().nullable().optional(),
        /** Sous-métrique côté CRM (`hipto_sub_metric.value`, ex. "Refus
         * autre", "Rappel personnel"). Plus précis que `metric`. */
        sub_metric: z.string().nullable().optional(),
        /** Date de la qualification (quand le callcenter a saisi le
         * statut). ISO 8601. */
        qualification_date: z.string(),
        /** Date du dernier appel effectué — peut être différente de
         * `qualification_date` si le callcenter saisit en différé. */
        call_date: z.string().nullable().optional(),
        /** Date du premier appel à ce lead. */
        first_call_date: z.string().nullable().optional(),
        /** N° de tentative d'appel pour ce lead. */
        attempt_number: z.number().int().nullable().optional(),
        /** Agent ID côté callcenter (numéro / email selon le partenaire). */
        agent_id: z.string().nullable().optional(),
        /** Average Handle Time — durée de l'appel formatée. */
        aht: z.string().nullable().optional(),
        /** D'où la qualification est remontée : Manager (REST MuleSoft /
         * Feedback) ou Hopti (Pub/Sub delivered-lead-sync). Admin-only à
         * l'affichage — purement debug ops. */
        source: z.enum(['manager', 'hopti']),
        /** ID du compte callcenter côté CRM (`hpg_accounts.id`) — debug. */
        cc_account_id: z.string().nullable().optional(),
      })
      .passthrough(),
  })
  .passthrough();
// Note : pas de `.refine` exigeant un pivot — un `doorly_lead_id` null doit
// PARSER (le topic est mutualisé : les messages non-Doorly n'en ont pas et
// sont droppés par le garde d'origine AVANT résolution). Le listener gère
// lui-même le cas "Doorly sans pivot" par un log + ack silencieux.

export type LeadQualificationSync = z.infer<typeof LeadQualificationSyncSchema>;
